import {
  Alert,
  AlertIcon,
  Box,
  Container,
  Flex,
  Heading,
  Stack,
  Text,
  HStack,
} from '@chakra-ui/react';
import Head from 'next/head';
import { Cart } from 'src/api/v1-api';
import { CartItem, CartOrderSummary, CoverLoader } from 'src/components/atoms';
import { CONTAINER_MAX_WIDTH_1 } from 'src/constants/ui';
import { useContext, useEffect, useState } from 'react';
import { useAuth } from 'src/utils/auth';
import { apiRequest } from 'src/utils/fetchUtils';
import { MarketplaceContext } from 'src/contexts/marketplace';
import NextLink from 'next/link';

const CartPage = (): JSX.Element => {
  const { getToken, getLomaSessionId } = useAuth();
  const { marketplace } = useContext(MarketplaceContext);

  const url = `${process.env.NEXT_PUBLIC_API_HOST}/cart/${marketplace?.id}/`;
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchCart = async () => {
    if (!isLoading && marketplace) {
      setLoading(true);
      const token = await getToken();
      const urlParams = {
        lomasessionid: getLomaSessionId(),
      };
      let cartResponse: Cart | null = null;
      try {
        cartResponse = await apiRequest('GET', url, token, null, false, urlParams);
        setCart(cartResponse);
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [marketplace]);

  const pageTitle = `Cart | ${marketplace?.name}`;
  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content="View Cart" />
        <link rel="icon" href={marketplace?.favicon_image || '/marketplace.ico'} />
      </Head>
      <Container maxW={CONTAINER_MAX_WIDTH_1} my={{ base: 4, md: 6, lg: 8 }}>
        <Box>
          <Stack
            direction={{ base: 'column', lg: 'row' }}
            align={{ lg: 'flex-start' }}
            spacing={{ base: '8', md: '16' }}
          >
            <Stack spacing={{ base: '8', md: '10' }} flex="2">
              <Heading fontSize="2xl" fontWeight="extrabold">
                Your Cart
              </Heading>
              <Stack spacing="8">
                {isLoading && <CoverLoader />}
                {!isLoading &&
                  isInitialized &&
                  (!cart || !cart.items || cart.items.length === 0) && (
                    <HStack>
                      <Alert status="info">
                        <AlertIcon />
                        <Text>Looks like your cart is empty!&nbsp;</Text>
                        <NextLink href="/">
                          <Text textDecor="underline">Home</Text>
                        </NextLink>
                      </Alert>
                    </HStack>
                  )}
                {cart &&
                  cart.items &&
                  cart.items.length > 0 &&
                  cart?.items.map((item) => (
                    <CartItem item={item} key={item?.variant?.id} onUpdate={fetchCart} />
                  ))}
              </Stack>
              <Stack spacing="6"></Stack>
            </Stack>

            <Flex direction="column" align="center" flex="1">
              {cart && cart.summary && <CartOrderSummary summary={cart?.summary} />}
            </Flex>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default CartPage;
